/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from "@angular/core";
import { DEFAULT_LANGUAGE, FakeMissingTranslationHandler, MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateCompiler, TranslateDefaultParser, TranslateFakeCompiler, TranslateFakeLoader,
    TranslateLoader, TranslateModule, TranslateModuleConfig, TranslateParser, TranslateService,
    USE_DEFAULT_LANG, USE_EXTEND, USE_STORE }
from "@ngx-translate/core";
import { GDuration, SimpleUser } from "@vierkant-software/types__api";
import { DateTime } from "luxon";
import { Globals } from "src/app/services/globals.service";
import { AppService } from "./app.service";
import { getFractions } from "src/util/formatting/currency";



TranslateModule.forChild = function (config: TranslateModuleConfig = {}) {
    return {
        ngModule:  TranslateModule,
        providers: [
          config.loader || {provide: TranslateLoader, useClass: TranslateFakeLoader},
          config.compiler || {provide: TranslateCompiler, useClass: TranslateFakeCompiler},
          config.parser || {provide: TranslateParser, useClass: GcTranslateParser},
          config.missingTranslationHandler || {provide: MissingTranslationHandler, useClass: FakeMissingTranslationHandler},
          {provide: USE_STORE, useValue: config.isolate},
          {provide: USE_DEFAULT_LANG, useValue: config.useDefaultLang},
          {provide: USE_EXTEND, useValue: config.extend},
          {provide: DEFAULT_LANGUAGE, useValue: config.defaultLanguage},
          TranslateService
        ]
    };
};

function DATE_parser(value: DateTime, subformat: string | undefined) {
    if (!DateTime.isDateTime(value))
        return "UNKNOWN_DATE";
    if (!subformat)
        subformat = 'DATETIME_SHORT';
    if (subformat in Globals.DateTime)
        return value.toLocaleString((Globals.DateTime as any)[subformat as any]);
    return value.toFormat(subformat);
}

// "asdfsadf asDFIJNSDFIUJASWs asdf {{days}} {{days:PLURAL:tag:tage}} "
// days = 1  => "tag"
// days = 2+ => "tage"
function PLURAL_parser(value: number, subformat: string | undefined) {
    if (!subformat)
        return;
    const [sigular, plural] = subformat.split(':');
    return value === 1 ? sigular : plural;
}

function GDURATION_parser(value: GDuration, subformat: string | undefined) {
    // TODO @chris - make it shiny
    return value?.value + ':' + value?.unit;
}

function USER_parser(value: SimpleUser | string, subformat: string | undefined) {
    if (typeof value === 'string')
        value = AppService.instance.userSyncService.get(value).value();
    // TODO @chris - implement subformat
    return value?.firstname + ' ' + value?.lastname;
}

export function CURRENCY_parser(value: number) {
    if (typeof value !== 'number') {
        console.warn('CURRENCY_parser received non-number value: ', value);
        return value;
    }

    const currency = AppService.instance.defaultCurrency;
    const locale = AppService.instance.locale;
    const fractions = getFractions(currency);


    if (!Number.isSafeInteger(value))
        console.warn('CURRENCY_parser received non-integer value: ', value);
    else
        // all good, convert integer into float with correct decimals
        value = value / Math.pow(10, fractions);

    let result;
    try {
        const currencyFormater = new Intl.NumberFormat(locale, {
            style:                 'currency',
            currency:              currency,
            minimumFractionDigits: fractions,
            maximumFractionDigits: fractions
        });
        result = currencyFormater.format(value);
    } catch(e) {
        console.warn('Currency format failed, falling back to manual formatting. Error was: ', e);
        const currencySymbol = (0).toLocaleString(locale, {
            style:                 'currency',
            currency:              currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).replace(/\d/g, '').trim();
        result = value.toFixed(fractions) + ' ' + currencySymbol;
    }
    return result;
}

@Injectable()
export class GcTranslateParser extends TranslateDefaultParser {
    static formatParsers: Record<string, (value: any, subformat: string | undefined) => string> = {
        'DATE':      DATE_parser,
        'PLURAL':    PLURAL_parser,
        'GDURATION': GDURATION_parser,
        'USER':      USER_parser,
        'CURRENCY':  CURRENCY_parser,
    };

    getValue(target: any, key: string) {
        const [skey, format, ...subformat] = typeof key === 'string' ? key.split(':') : [key, undefined, undefined];
        let res = (this as any)._getValue(target, skey);
        if (!format || !res)
            return res;
        if (format in GcTranslateParser.formatParsers)
            return GcTranslateParser.formatParsers[format](res, subformat.join(':'));
        throw new Error('Unknown format: ' + format + ' while parsing translation token ' + key);
    }
}
(TranslateDefaultParser as any).prototype._getValue = TranslateDefaultParser.prototype.getValue;
TranslateDefaultParser.prototype.getValue = GcTranslateParser.prototype.getValue;

FakeMissingTranslationHandler.prototype.handle = function (params: MissingTranslationHandlerParams) {
    if (/^#\(.*\)#/.test(params.key))
        return params.translateService.parser.interpolate(params.key.replace(/#\((.*)\)#/, '$1'), params.interpolateParams);
    else
        return params.key;
    // return "42";
};